/**
 * The Recap button's second line (design pass 2, change 5, 2026-09-21): one
 * short phrase for the drawer's autosave state, derived from useRecapDrafts'
 * existing per-tier `saving` / `lastSaved` / `docId` and `hasUnsavedChanges`.
 * Nothing new is stored; this only reads what the hook already tracks.
 *
 *   "Not started"      no tier has ever been saved for this lesson
 *   "Saving"           a tier is dirty (a write is due within the 3s debounce)
 *                      or a write is in flight
 *   "Saved just now"   the newest save landed under a minute ago
 *   "Saved 2 min ago"  under an hour
 *   "Saved at 4:12 PM" earlier today
 *   "Saved Sep 18"     an earlier day (a reopened lesson's stored recap)
 *   "Saved"            a doc exists but its save time is unknown
 *
 * Copy carries no em dashes.
 */
const toMs = (value) => {
	if (!value) return 0;
	if (value instanceof Date) return value.getTime();
	if (typeof value.toMillis === 'function') return value.toMillis();
	if (typeof value.seconds === 'number') return value.seconds * 1000;
	const parsed = new Date(value).getTime();
	return Number.isNaN(parsed) ? 0 : parsed;
};

const sameDay = (a, b) =>
	a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const savedLabel = (savedMs, nowMs) => {
	const age = nowMs - savedMs;
	if (age < 60 * 1000) return 'Saved just now';
	if (age < 60 * 60 * 1000) return `Saved ${Math.max(1, Math.round(age / 60000))} min ago`;
	const saved = new Date(savedMs);
	const now = new Date(nowMs);
	if (sameDay(saved, now)) {
		return `Saved at ${saved.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
	}
	return `Saved ${saved.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

export const recapSaveState = ({ tiers = {}, hasUnsavedChanges = false, now = Date.now() } = {}) => {
	const list = Object.values(tiers || {});
	if (list.some((t) => t && t.saving)) return 'Saving';
	if (hasUnsavedChanges) return 'Saving';
	const last = list.reduce((max, t) => Math.max(max, toMs(t && t.lastSaved)), 0);
	if (last) return savedLabel(last, now);
	if (list.some((t) => t && t.docId)) return 'Saved';
	return 'Not started';
};

export default recapSaveState;
